<template>
  <div class="content">
    <b-card class="summary-card mt-3">
      <header slot="header">
        <b-row>
          <b-col :title="labels.basicInformations" md="12">
            <span class="bold">{{ labels.basicInformations }}</span>
          </b-col>
        </b-row>
      </header>
      <b-row>
        <b-col class="pb-3" cols="12" xl="6">
          <div
            v-for="(language, index) in availableLanguages"
            :key="`title_${index}`"
          >
            <b-form-group v-if="isLanguageSelected(index)">
              <p class="readonly-input">
                {{ title[language] }}
              </p>
              <label class="custom-input-label">
                <img
                  :src="getLanguageIcon(availableLanguages[selectedLanguage])"
                  class="language-icon"
                />
                {{ labels.surveyTitle.label }}
              </label>
            </b-form-group>
          </div>
          <b-form-group>
            <b-form-select
              class="readonly-input"
              :value="period"
              size="lg"
              required
              disabled
            >
              <option value="weekly">
                {{ labels.period.weekly }}
              </option>
              <option value="biweekly">
                {{ labels.period.biweekly }}
              </option>
              <option value="monthly">
                {{ labels.period.monthly }}
              </option>
              <option value="single-shot">
                {{ labels.period.singleShot }}
              </option>
            </b-form-select>
            <label class="custom-input-label">
              {{ labels.period.label }}
            </label>
          </b-form-group>
          <b-form-group>
            <b-form-select
              class="readonly-input"
              :value="questionsDistribution"
              size="lg"
              required
              disabled
            >
              <option value="random">
                {{ labels.questionsDistribution.random }}
              </option>
              <option value="ordered">
                {{ labels.questionsDistribution.ordered }}
              </option>
            </b-form-select>
            <label class="custom-input-label">
              {{ labels.questionsDistribution.label }}
            </label>
          </b-form-group>
          <b-form-group>
            <b-form-input
              v-if="period === 'single-shot'"
              class="readonly-input"
              :value="labels.questionsQuantity.all"
              readonly
            />
            <b-form-input
              v-else
              class="readonly-input"
              :value="questionsQuantity"
              readonly
            >
            </b-form-input>
            <label class="custom-input-label">
              {{ labels.questionsQuantity.label }}
            </label>
          </b-form-group>
          <b-form-group v-if="availableLanguages.length > 0">
            <v-select
              class="readonly-input"
              multiple
              :value="availableLanguages"
              disabled
            >
              <template slot="selected-option" slot-scope="option">
                <div class="tag-wrap">
                  <img
                    class="language-icon mr-2"
                    :src="getLanguageIcon(option.label)"
                  />
                  <span class="tag-label">
                    {{ getIdiom(option.label) }}
                  </span>
                </div>
              </template>
            </v-select>
            <label class="custom-input-label">
              {{ labels.languages.label }}
            </label>
          </b-form-group>
        </b-col>
        <b-col class="pb-3" cols="12" xl="6">
          <div
            v-for="(language, index) in availableLanguages"
            :key="`title_${index}`"
          >
            <b-form-group v-if="isLanguageSelected(index)">
              <p class="readonly-input">
                {{
                  description[language]
                    ? description[language]
                    : labels.surveyDescription.placeholderEmpty
                }}
              </p>
              <label class="custom-input-label">
                <img
                  :src="getLanguageIcon(availableLanguages[selectedLanguage])"
                  class="language-icon"
                />
                {{ labels.surveyDescription.label }}
              </label>
            </b-form-group>
          </div>
        </b-col>
      </b-row>
    </b-card>
    <b-card class="summary-card">
      <header slot="header">
        <b-row>
          <b-col :title="labels.public.title" md="12">
            <div class="public-header">
              <span class="bold">{{ labels.public.title }}</span>
            </div>
          </b-col>
        </b-row>
      </header>
      <div class="public-content" v-if="sendToCompany">
        <b-row>
          <b-col cols="12" xl="12">
            <span class="tag-image mdi mdi-factory"></span>
            {{ labels.public.company }}
          </b-col>
        </b-row>
      </div>
      <div class="public-content" v-else>
        <b-row
          class="pb-2"
          v-for="group in groupsWithHierarchy"
          :key="group.id"
        >
          <b-col cols="12" xl="8">
            <span class="tag-image mdi mdi-account-multiple"></span>
            <b-link
              target="_blank"
              :to="{
                name: 'dashboard.groups',
                params: { groupId: group._id },
              }"
            >
              {{ group.name }}
            </b-link>
          </b-col>
          <b-col cols="12" xl="4" class="public-content-type">
            <b-badge variant="success">
              {{ labels.public.groupsWithHierarchy }}
            </b-badge>
          </b-col>
        </b-row>
        <b-row class="pb-2" v-for="group in groupsOnly" :key="group.id">
          <b-col cols="12" xl="8">
            <span class="tag-image mdi mdi-account-multiple"></span>
            <b-link
              target="_blank"
              :to="{
                name: 'dashboard.groups',
                params: { groupId: group._id },
              }"
            >
              {{ group.name }}
            </b-link>
          </b-col>
          <b-col cols="12" xl="4" class="public-content-type">
            <b-badge variant="warning">{{ labels.public.groupsOnly }}</b-badge>
          </b-col>
        </b-row>
        <b-row class="pb-2" v-for="user in users" :key="user.id">
          <b-col cols="12" xl="8">
            <span class="tag-image mdi mdi-account"></span>
            {{ `${user.profile.firstname} ${user.profile.lastname}` }}
          </b-col>
          <b-col cols="12" xl="4" class="public-content-type">
            <b-badge variant="info">{{ labels.public.user }}</b-badge>
          </b-col>
        </b-row>
      </div>
    </b-card>
    <b-card class="summary-card">
      <header slot="header">
        <b-row>
          <b-col :title="labels.access.title" md="12">
            <span class="bold">{{ labels.access.title }}</span>
          </b-col>
        </b-row>
      </header>
      <div class="public-content">
        <b-row>
          <b-col class="pb-2" cols="12" xl="12">
            <span class="tag-image mdi mdi-account-multiple"></span>
            {{ labels.access.admin }}
          </b-col>
        </b-row>
        <b-row v-if="managersPermission">
          <b-col class="pb-2" cols="12" xl="12">
            <span class="tag-image mdi mdi-account-multiple"></span>
            {{ labels.access.manager }}
          </b-col>
        </b-row>
        <b-row class="pb-2">
          <b-col cols="12" xl="8">
            <span class="tag-image mdi mdi-account"></span>
            {{ creator }}
          </b-col>
          <b-col cols="12" xl="4" class="public-content-type">
            <b-badge variant="primary">{{ labels.access.owner }}</b-badge>
          </b-col>
        </b-row>
        <b-row class="pb-2" v-for="user in surveyAccessUsers" :key="user.id">
          <b-col cols="12" xl="8">
            <span class="tag-image mdi mdi-account"></span>
            {{ `${user.profile.firstname} ${user.profile.lastname}` }}
          </b-col>
          <b-col cols="12" xl="4" class="public-content-type">
            <b-badge variant="info">{{ labels.access.user }}</b-badge>
          </b-col>
        </b-row>
      </div>
    </b-card>
    <b-card class="summary-card questions">
      <header slot="header">
        <b-row>
          <b-col md="12">
            <b-navbar class="survey-informations-navbar">
              <b-navbar-nav>
                <b-nav-item
                  class="survey-informations-navbar-item"
                  :active="isActive(0)"
                  @click="setActiveSurveyInformationsTab(0)"
                >
                  <span class="pr-3 pl-4">{{ labels.questions }}</span>
                  <b-badge pill :variant="getVariant(0)" class="mr-3">
                    {{ questions.length }}
                  </b-badge>
                </b-nav-item>
                <b-nav-item
                  class="survey-informations-navbar-item"
                  :active="isActive(1)"
                  @click="setActiveSurveyInformationsTab(1)"
                >
                  <span class="pr-3 pl-4">{{ labels.categories }}</span>
                  <b-badge pill :variant="getVariant(1)" class="mr-3">
                    {{ categories.length }}
                  </b-badge>
                </b-nav-item>
              </b-navbar-nav>
            </b-navbar>
          </b-col>
        </b-row>
      </header>
      <QuestionsConfiguration
        v-if="activeSurveyInformationsTab === 0"
        :categories="categories"
        :questions="questions"
        :editable="false"
        :availableLanguages="availableLanguages"
        :selectedLanguage="selectedLanguage"
      />
      <CategoriesConfiguration
        v-if="activeSurveyInformationsTab === 1"
        :categories="categories"
        :questions="questions"
        :editable="false"
        :availableLanguages="availableLanguages"
        :selectedLanguage="selectedLanguage"
      />
    </b-card>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import QuestionsConfiguration from '../QuestionsConfiguration.vue'
import CategoriesConfiguration from '../CategoriesConfiguration.vue'

const props = {
  questions: {
    type: Array,
  },
  availableLanguages: {
    type: Array,
  },
  languages: {
    type: Array,
  },
  selectedLanguage: {
    type: Number,
  },
  title: {
    type: Object,
  },
  description: {
    type: Object,
  },
  period: {
    type: String,
  },
  questionsDistribution: {
    type: String,
  },
  questionsQuantity: {
    type: Number,
  },
  enableFeedbacks: {
    type: Boolean,
  },
  mandatoryQuestions: {
    type: Boolean,
  },
  categories: {
    type: Array,
  },
  sendToCompany: {
    type: Boolean,
  },
  groupsWithHierarchy: {
    type: Array,
  },
  groupsOnly: {
    type: Array,
  },
  users: {
    type: Array,
  },
  managersPermission: {
    type: Boolean,
  },
  surveyAccessUsers: {
    type: Array,
  },
  creator: {
    type: String,
  },
}

export default {
  props,
  components: {
    QuestionsConfiguration,
    CategoriesConfiguration,
    vSelect: () => import('vue-select'),
  },
  data() {
    return {
      activeSurveyInformationsTab: 0,
    }
  },
  computed: {
    ...mapGetters({
      resources: 'resources',
    }),
    labels() {
      return this.resources.component.customSurveys.create.tabs
    },
  },
  methods: {
    getLanguageIcon(language) {
      return `/icons/languages/${language}.svg`
    },
    isLanguageSelected(language) {
      return this.selectedLanguage === language
    },
    getIdiom(language) {
      return this.languages.find(l => l.code === language).idiom
    },
    isActive(tab) {
      return this.activeSurveyInformationsTab === tab
    },
    setActiveSurveyInformationsTab(tab) {
      this.activeSurveyInformationsTab = tab
    },
    getVariant(tab) {
      return this.isActive(tab) ? 'secondary' : 'white'
    },
  },
}
</script>

<style lang="scss" scoped>
@import '~@/styles/custom/colors';

.summary-card {
  margin-bottom: 30px;

  .public-content {
    .public-content-type {
      text-align: center;
    }
  }

  .no-content {
    text-align: center;
    padding-top: 50px;
    padding-bottom: 40px;
  }

  &.questions {
    border: 0 !important;

    .card-header {
      padding: 0;
      overflow: hidden !important;
      border: 1px solid var(--gray07) !important;
    }
    .card-body {
      padding: 0;
    }
  }

  .language-icon {
    width: 15px;
    height: 15px;
  }

  .readonly-input {
    background-color: map-get($theme-colors, 'white') !important;
    border-color: map-get($theme-colors, 'white') !important;
    color: #6c757d !important;
    height: 42px !important;
  }

  p.readonly-input {
    margin-top: 10px;
    margin-left: 20px;
    margin-right: 10px;
    font-size: 1rem;
    height: 100% !important;
  }

  .survey-informations-navbar {
    padding-top: 0;
    padding-left: 0;
    font-size: 14pt;

    .survey-informations-navbar-item {
      padding-top: 7px;
      border-right: 1px solid map-get($theme-colors, 'gray07');

      &:last-child {
        border-right: unset;
      }
    }

    .active {
      border-bottom: 5px solid map-get($theme-colors, 'secondary');
      color: map-get($theme-colors, 'secondary') !important;
      font-weight: bold;
    }

    .badge-white {
      border: 1px solid var(--gray07) !important;
    }
  }

  .survey-description {
    height: 100% !important;
  }
}
</style>

<style lang="scss">
@import '~@/styles/custom/colors';

.summary-card .readonly-input .vs__dropdown-toggle {
  margin-left: 15px;
  background-color: map-get($theme-colors, 'white') !important;
  border-color: map-get($theme-colors, 'white') !important;
  cursor: auto;

  .vs__deselect,
  .vs__search {
    display: none;
  }

  .vs__selected {
    background-color: map-get($theme-colors, 'white') !important;
    cursor: auto;
    color: #6c757d;

    .tag-label {
      background-color: map-get($theme-colors, 'white') !important;
    }
  }
}
</style>
